var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { InjectorScope } from '../core/injector';
import { injectable } from '../decorators/injectable';
import { stateable } from '../decorators/stateable';
import { getCurrentBreakpoint } from './breakpoints';
import { debounce } from './debounce';
/** The name of the module */
export const name = 'BreakpointObserver';
let BreakpointObserver = class BreakpointObserver {
    /**
     * Create a BreakpointObserver instance
     */
    constructor() {
        // Collection of callback functions
        this.observers = new Map();
        // Set the initial state
        const initialState = {
            last: null,
            current: null,
            isObserving: false,
        };
        // Initialize the module's state
        this.initState(initialState);
        // Create the ResizeObserver instance
        this.resizeObserver = new ResizeObserver(debounce(100, this.handleResize.bind(this)));
        this.init();
    }
    init() {
        this.setState({
            current: getCurrentBreakpoint(),
        });
    }
    /**
     * Add a function to be called when a breakpoint transition occurs
     *
     * @param callback Callback function to be added
     * @param options Any options for the callback
     */
    observe(callback, options = {}) {
        this.observers.set(callback, options);
        this.startObserving();
    }
    /**
     * Remove a callback function from being called when a breakpoint transition occurs
     *
     * @param callback Callback function to be removed
     */
    unobserve(callback) {
        this.observers.delete(callback);
        this.stopObserving();
    }
    /**
     * Start observing if it isn't currently active and there are observers registered
     */
    startObserving() {
        if (!this.getState().isObserving && this.observers.size) {
            this.resizeObserver.observe(document.body);
            this.setState({
                isObserving: true,
            });
        }
    }
    /**
     * Stop observing if it's currently active and there aren't any observers registered
     */
    stopObserving() {
        if (this.getState().isObserving && !this.observers.size) {
            this.resizeObserver.unobserve(document.body);
            this.setState({
                isObserving: false,
            });
        }
    }
    /**
     * Take action on a viewport resize event
     */
    handleResize() {
        // Get the breakpoint for the current viewport size
        const breakpoint = getCurrentBreakpoint();
        // Get the last known registered breakpoint
        const { current } = this.getState();
        // If the breakpoint has changed, then fire the callbacks
        if (breakpoint !== current) {
            this.setState({
                last: current,
                current: breakpoint,
            });
            this.notifyObservers();
        }
    }
    /**
     * Notify observer callbacks when a breakpoint change occurs
     */
    notifyObservers() {
        // Get the current breakpoint for possible filter comparisons
        const { current } = this.getState();
        const { last } = this.getState();
        // Loop through all observers
        this.observers.forEach((options, callback) => {
            // If a breakpoints filter exists, check that it includes the current breakpoint
            if (options && options.breakpoints && !options.breakpoints.includes(current)) {
                return;
            }
            // Fire the callback and pass the current breakpoint
            callback(current, last);
        });
    }
};
BreakpointObserver = __decorate([
    stateable(name),
    injectable({
        key: name,
        scope: InjectorScope.Singleton,
    })
], BreakpointObserver);
export default BreakpointObserver;
